'use client';

import { Info } from 'lucide-react';
import { useId, type ReactElement, type ReactNode } from 'react';
import { onlineServiceFeeTooltip } from '../onlineServiceFee/onlineServiceFee';
import { cn } from '../utils/utils';

export interface OnlineServiceFeeRowProps {
    /** The server-resolved line. The amount shown is the fee BEFORE tax; its tax belongs in the surface's tax row. */
    fee: { amount: number; label: string };
    locationName?: string | null;
    /** Row typography/spacing — each surface keeps its own summary scale. */
    className?: string;
    /** Amount styling, matching the surface's other summary amounts. */
    amountClassName?: string;
    /** Label colour/styling, matching the surface's sibling rows. Replaces the default `text-gray-700`. */
    labelClassName?: string;
    /** The surface's own currency formatter; defaults to `$0.00`. */
    formatAmount?: (amount: number) => string;
}

export interface OnlineServiceFeeRowFrameProps extends OnlineServiceFeeRowProps {
    /**
     * Wraps the ⓘ trigger in its popover. `OnlineServiceFeeRow` passes the
     * shared Popover here; absent, the trigger renders bare (the placeholder a
     * surface shows while the popover's chunk loads — see
     * `onInfoActivate`).
     */
    renderInfo?: (trigger: ReactElement, explanation: string) => ReactNode;
    /** Bare trigger only: called on a tap/click/Enter/Space before the popover exists. */
    onInfoActivate?: () => void;
    /**
     * Bare trigger only: show the explanation VISIBLY under the row, disclosed
     * by the ⓘ (`aria-expanded`/`aria-controls`). For a surface whose popover
     * could not load, so a sighted guest never meets a dead ⓘ.
     */
    explanationVisible?: boolean;
}

/**
 * The Online Service Fee row WITHOUT its popover: label, ⓘ trigger, the
 * screen-reader explanation and the amount. `OnlineServiceFeeRow` is this plus
 * the shared Popover; it exists separately so a surface with a first-download
 * budget (checkout) can render the identical row at once and load the popover
 * machinery (Radix + floating-ui, ~22 KB gzip) lazily. One markup, two ways to
 * attach the popover — never two copies of the row.
 */
export function OnlineServiceFeeRowFrame({
    fee,
    locationName,
    className,
    amountClassName,
    labelClassName,
    formatAmount = (amount) => `$${amount.toFixed(2)}`,
    renderInfo,
    onInfoActivate,
    explanationVisible = false,
}: OnlineServiceFeeRowFrameProps) {
    const explanation = onlineServiceFeeTooltip(locationName);
    const descriptionId = useId();
    const disclosureId = useId();
    const trigger = (
        <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500 pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label={`About the ${fee.label}`}
            aria-describedby={descriptionId}
            data-fee-info-trigger=""
            {...(renderInfo
                ? {}
                : {
                      'aria-haspopup': 'dialog' as const,
                      'aria-expanded': explanationVisible,
                      'aria-controls': explanationVisible ? disclosureId : undefined,
                      onClick: onInfoActivate,
                  })}
        >
            <Info className="h-4 w-4" aria-hidden="true" />
        </button>
    );
    const row = (
        <div
            className={cn('flex items-center justify-between text-base', className)}
            data-testid="online-service-fee"
        >
            <span className={cn('flex items-center gap-1', labelClassName ?? 'text-gray-700')}>
                <span>{fee.label}</span>
                {renderInfo ? renderInfo(trigger, explanation) : trigger}
                <span id={descriptionId} className="sr-only">
                    {explanation}
                </span>
            </span>
            <span className={cn('font-semibold text-gray-900', amountClassName)}>{formatAmount(fee.amount)}</span>
        </div>
    );
    if (renderInfo || !explanationVisible) return row;
    return (
        <>
            {row}
            <p id={disclosureId} className="mt-1 text-sm text-gray-600">
                {explanation}
            </p>
        </>
    );
}
